/**
 * MinimalistPill — Feb 28, 2026
 *
 * Always-visible header pill that surfaces the Minimalist View toggle so
 * the provider can flip it without hunting through Profile › Settings or
 * remembering the Ctrl+Shift+M shortcut. User reported: "I literally
 * cannot find it on the screen anywhere."
 *
 * State source: `data-minimalist` attribute on <html> (set by App.js on
 * boot and toggled here). localStorage key:
 * `writeright_minimalist_tiles` ('1' | '0').
 */
import React, { useEffect, useState } from 'react';
import { Square, SquareStack } from 'lucide-react';
import { toast } from 'sonner';

const readMinimalist = () => {
  try { return localStorage.getItem('writeright_minimalist_tiles') === '1'; }
  catch { return false; }
};

export const MinimalistPill = () => {
  const [on, setOn] = useState(readMinimalist);

  useEffect(() => {
    // Mirror localStorage on mount in case App.js set it asynchronously.
    setOn(readMinimalist());
    // Also listen for cross-tab changes + keyboard-shortcut toggles so the
    // pill stays in sync.
    const sync = () => setOn(readMinimalist());
    window.addEventListener('storage', sync);
    window.addEventListener('writeright:minimalist-changed', sync);
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener('writeright:minimalist-changed', sync);
    };
  }, []);

  const toggle = () => {
    const next = !on;
    setOn(next);
    try { localStorage.setItem('writeright_minimalist_tiles', next ? '1' : '0'); } catch { /* ignore */ }
    try { document.documentElement.setAttribute('data-minimalist', next ? '1' : '0'); } catch { /* ignore */ }
    try { window.dispatchEvent(new CustomEvent('writeright:minimalist-changed')); } catch { /* ignore */ }
    toast.success(next ? 'Minimalist view ON' : 'Minimalist view OFF', { duration: 1500 });
  };

  return (
    <button
      type="button"
      onClick={toggle}
      title={on ? 'Minimalist view ON — click to show full UI' : 'Minimalist view OFF — click for distraction-free mode'}
      aria-pressed={on}
      data-testid="minimalist-view-pill"
      data-state={on ? 'on' : 'off'}
      className={`inline-flex items-center gap-1 px-2 py-0.5 border-2 text-[10px] font-medium leading-tight transition-all whitespace-nowrap ${
        on
          ? 'bg-sky-600 border-sky-300 text-white'
          : 'bg-slate-700 border-slate-400 text-white hover:bg-sky-700 hover:border-sky-300'
      }`}
    >
      {on ? <Square className="w-3 h-3" /> : <SquareStack className="w-3 h-3" />}
      <span>{on ? 'Minimal' : 'Minimal'}</span>
      {on && <span className="text-[9px] px-1 rounded bg-sky-500/30 text-sky-100 font-bold">ON</span>}
    </button>
  );
};

export default MinimalistPill;
